import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { CongressService } from '../../core/services/congress.service';
import { CongressFilterService } from '../../core/services/congress-filter.service';
import { ThemeService } from '../../core/services/theme.service';
import { LoggingService } from '../../core/services/logging.service';
import { CongressDisclosureView } from '../../shared/models/congress-disclosure.model';
import { Company } from '../../shared/models/company-model';

// PrimeNG imports
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { TabsModule } from 'primeng/tabs';
import { TooltipModule } from 'primeng/tooltip';

// Shared components
import { CompanyDisplayComponent } from "../../shared/ui/company-display/company-display.component";
import { ProgressSpinnerComponent } from "../../shared/ui/progress-spinner/progress-spinner.component";
import { ChamberBadgeComponent } from "../../shared/ui/chamber-badge/chamber-badge.component";

type ViewType = 'time' | 'ticker';
type GroupByType = 'member' | 'ticker';

@Component({
  selector: 'app-congress',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    CardModule,
    ButtonModule,
    SelectModule,
    InputTextModule,
    MessageModule,
    TabsModule,
    TooltipModule,
    CompanyDisplayComponent,
    ProgressSpinnerComponent,
    ChamberBadgeComponent
],
  templateUrl: './congress.component.html',
  styleUrls: ['./congress.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CongressComponent implements OnInit {
  private congressService = inject(CongressService);
  private congressFilterService = inject(CongressFilterService);
  private logger = inject(LoggingService);
  themeService = inject(ThemeService);

  // Use filter service signals instead of local signals
  currentView = this.congressFilterService.currentView;
  selectedTimePeriod = this.congressFilterService.selectedTimePeriod;
  selectedChamber = this.congressFilterService.selectedChamber;
  selectedGroupBy = this.congressFilterService.selectedGroupBy;
  selectedCompany = this.congressFilterService.selectedCompany;

  // Expansion state
  expandedGroups = signal<Set<string>>(new Set());

  // Data state
  loading = signal(false);
  error = signal<string | null>(null);
  disclosures = signal<CongressDisclosureView[]>([]);
  statistics = signal<any>(null);

  // Time period options
  timePeriodOptions = [
    { label: '3 Months', value: 3 },
    { label: '6 Months', value: 6 },
    { label: '1 Year', value: 12 },
    { label: '2 Years', value: 24 },
  ];

  // Chamber filter options
  chamberOptions = [
    { label: 'All Chambers', value: 'all' },
    { label: 'House', value: 'House' },
    { label: 'Senate', value: 'Senate' }
  ];

  // Group by options
  groupByOptions = [
    { label: 'Member', value: 'member' as GroupByType },
    { label: 'Ticker', value: 'ticker' as GroupByType }
  ];

  // Computed signals
  filteredDisclosures = computed(() => {
    let filtered = this.disclosures();

    // Apply chamber filter
    if (this.selectedChamber() !== 'all') {
      filtered = filtered.filter(d => d.chamber === this.selectedChamber());
    }

    // Apply time filter for ticker view (client-side)
    if (this.currentView() === 'ticker' && this.selectedTimePeriod() !== 12) {
      const now = new Date();
      const cutoffDate = new Date();

      if (this.selectedTimePeriod() === 3) {
        cutoffDate.setMonth(now.getMonth() - 3);
      } else if (this.selectedTimePeriod() === 6) {
        cutoffDate.setMonth(now.getMonth() - 6);
      }

      filtered = filtered.filter(d => new Date(d.transactionDate) >= cutoffDate);
    }

    return filtered;
  });

  groupedDisclosures = computed(() => {
    const disclosures = this.filteredDisclosures();
    const groupBy = this.selectedGroupBy();

    if (groupBy === 'member') {
      const grouped: { [key: string]: CongressDisclosureView[] } = {};
      disclosures.forEach(disclosure => {
        const memberKey = `${disclosure.firstName} ${disclosure.lastName}`;
        if (!grouped[memberKey]) {
          grouped[memberKey] = [];
        }
        grouped[memberKey].push(disclosure);
      });
      return grouped;
    } else {
      const grouped: { [key: string]: CongressDisclosureView[] } = {};
      disclosures.forEach(disclosure => {
        const tickerKey = disclosure.ticker;
        if (!grouped[tickerKey]) {
          grouped[tickerKey] = [];
        }
        grouped[tickerKey].push(disclosure);
      });
      return grouped;
    }
  });

  // Sorted group keys by count (descending)
  sortedGroupKeys = computed(() => {
    const grouped = this.groupedDisclosures();
    return Object.keys(grouped).sort((a, b) => {
      return grouped[b].length - grouped[a].length;
    });
  });

  groupedByMember = computed(() => {
    const grouped: { [key: string]: CongressDisclosureView[] } = {};
    this.filteredDisclosures().forEach(disclosure => {
      const memberKey = disclosure.memberName;
      if (!grouped[memberKey]) {
        grouped[memberKey] = [];
      }
      grouped[memberKey].push(disclosure);
    });
    return grouped;
  });

  ngOnInit() {
    this.loadTimeBasedData();
    // this.loadStatistics();
  }

  // View switching
  selectView(viewType: ViewType) {
    this.congressFilterService.updateView(viewType);
    if (viewType === 'time') {
      this.congressFilterService.updateCompany(null);
      this.loadTimeBasedData();
    } else if (viewType === 'ticker') {
      // Clear data when switching to ticker view
      this.disclosures.set([]);
    }
  }

  // Legacy methods for backward compatibility
  switchToTimeView() {
    this.selectView('time');
  }

  switchToTickerView() {
    this.selectView('ticker');
  }

  // Data loading
  loadTimeBasedData() {
    this.loading.set(true);
    this.error.set(null);

    this.congressService.getRecent(this.selectedTimePeriod()).subscribe({
      next: (data) => {
        this.disclosures.set(this.congressService.transformToViews(data));
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set('Failed to load congressional disclosures');
        this.loading.set(false);
        this.logger.error('Error loading disclosures:', err);
      }
    });
  }

  loadTickerData(company: Company) {
    this.loading.set(true);
    this.error.set(null);

    this.congressService.searchByTicker(company.ticker).subscribe({
      next: (data) => {
        this.disclosures.set(this.congressService.transformToViews(data));
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set('Failed to load ticker data');
        this.loading.set(false);
        this.logger.error('Error loading ticker data:', err);
      }
    });
  }

  loadStatistics() {
    this.congressService.getStatistics().subscribe({
      next: (stats) => {
        this.statistics.set(stats);
      },
      error: (err) => {
        this.logger.error('Error loading statistics:', err);
      }
    });
  }

  // Event handlers
  onCompanySelected(company: Company) {
    this.congressFilterService.updateCompany(company);
    this.loadTickerData(company);
  }

  onTimePeriodChange() {
    if (this.currentView() === 'time') {
      this.loadTimeBasedData();
    }
  }

  onChamberChange() {
    // Client-side filtering, no API call needed
  }

  onGroupByChange() {
    // Client-side grouping change, no API call needed
  }

  // Expansion methods
  toggleGroupExpansion(groupKey: string) {
    const current = this.expandedGroups();
    const newSet = new Set(current);
    if (newSet.has(groupKey)) {
      newSet.delete(groupKey);
    } else {
      newSet.add(groupKey);
    }
    this.expandedGroups.set(newSet);
  }

  isGroupExpanded(groupKey: string): boolean {
    return this.expandedGroups().has(groupKey);
  }

  // Utility methods
  objectKeys(obj: any): string[] {
    return Object.keys(obj);
  }

  calculateTotalValue(disclosures: CongressDisclosureView[]): number {
    return disclosures.reduce((total, disclosure) => {
      // Parse the formatted amount (remove $ and commas)
      const amount = parseFloat(disclosure.formattedAmount.replace(/[$,]/g, ''));
      return total + (isNaN(amount) ? 0 : amount);
    }, 0);
  }

  // TrackBy function for ngFor performance
  trackByDisclosure(index: number, disclosure: CongressDisclosureView): string {
    return disclosure.link;
  }

  // TrackBy function for group keys in ngFor
  trackByGroupKey(index: number, key: string): string {
    return key;
  }

  // Table column definitions
  tableColumns = [
    { field: 'memberName', header: 'Member' },
    { field: 'ticker', header: 'Ticker' },
    { field: 'type', header: 'Type' },
    { field: 'formattedAmount', header: 'Amount' },
    { field: 'formattedTransactionDate', header: 'Transaction Date' },
    { field: 'chamber', header: 'Chamber' }
  ];
}
